import Cookies from "js-cookie";
import { axiosInstance } from "./axiosInstance";
import { postErrorLog } from "./errorLog.service";
import { logAnalyticsEvent } from "src/utils/Firebase";
import { getOrCreateDeviceId } from "src/utils/deviceId";
import { blockToolProgressPersist, clearLocalStorageKeepGuestId } from "src/utils/toolProgressStorage";
import Swal from "sweetalert2";

export const AuthService = {
	login: async (email: string, password: string) => {
		const deviceId = getOrCreateDeviceId();
		return await axiosInstance.post("/api/auth/user/login", {
			email: email,
			password: password,
			device_id: deviceId,
		});
	},
	revokeSession: async (deviceId: string) => {
		// Revoke session berdasarkan device_id
		return await axiosInstance.post("/api/auth/user/revoke-session", {
			device_id: deviceId,
		});
	},
	getDeviceList: async (deviceId: string) => {
		// Get list device untuk device limit
		return await axiosInstance.get(`/api/auth/user/social/device/${deviceId}`);
	},
	register: async (
		name: string,
		email: string,
		username: string,
		phone: string,
		password: string,
		source: string,
		referral: string,
		recaptcha: string
	) => {
		return await axiosInstance.post("/api/auth/user/register", {
			name: name,
			email: email,
			username: username,
			phone: phone,
			password: password,
			source: source,
			referral_code: referral,
			recaptcha: recaptcha,
		});
	},
	me: async () => {
		try {
			const response = await axiosInstance.get("/api/auth/user/me");
			localStorage.setItem("USER", JSON.stringify(response.data.user));
			// Broadcast user updates so views that read from getUser() can refresh immediately.
			window.dispatchEvent(new CustomEvent("user-updated", { detail: response.data.user }));
			return response;
		} catch (err) {
			console.error(err);
			return null;
		}
	},
	resetPassword: async (oldPassword: string, newPassword: string) => {
		const response = await axiosInstance.post(
			"/api/feature/user/reset-password",
			{
				old_password: oldPassword,
				new_password: newPassword,
			}
		);
		return response;
	},
};

export const loginGoogle = async (deviceId?: string) => {
	const params = new URLSearchParams();
	if (deviceId) {
		params.append("device_id", deviceId);
	}
	const queryString = params.toString();
	const separator = queryString ? "?" : "";
	window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/user/social/login/google/${separator}${queryString}`;
}

/** Resolves `true` when redirecting to /login, `false` when the user cancels or the request fails. */
export const logout = async (isLogin = false): Promise<boolean> => {
	// Broadcast logout loading state for full-screen overlay
	const setGlobalLogoutLoading = (isLoading: boolean) => {
		try {
			window.dispatchEvent(new CustomEvent("logout-loading", { detail: isLoading }));
		} catch (error) {
			console.error("Failed to dispatch logout loading event:", error);
		}
	};

	const safeLogError = async (error: any, context: string) => {
		try {
			await postErrorLog({
				response: {
					data: {
						message: `Logout failed: ${context}`,
						context: {
							isLogin,
							path: window.location.pathname,
						},
					},
					statusText: error?.response?.statusText,
					status: error?.response?.status,
				},
				message: error?.message,
				stack: error?.stack,
			});
		} catch (logErr) {
			console.error("Bug log for logout failed:", logErr);
		}
	};

	if (isLogin) {
		try {
			setGlobalLogoutLoading(true);
			await axiosInstance.post(`/api/auth/logout`);
			logAnalyticsEvent("logout", { method: "delete_token" });

			const submateriProgress = JSON.parse(
				localStorage.getItem("SubmateriProgress") || "[]"
			);
			const seeTourDashboardCounter = localStorage.getItem("seeTourDashboardCounter");
			const isMobileValue = localStorage.getItem("isMobile");

			clearLocalStorageKeepGuestId();

			localStorage.setItem("SubmateriProgress", JSON.stringify(submateriProgress));
			if (seeTourDashboardCounter) {
				localStorage.setItem("seeTourDashboardCounter", seeTourDashboardCounter);
			}
			if (isMobileValue) {
				localStorage.setItem("isMobile", isMobileValue);
			}

			window.location.href = "/login";
		} catch (error) {
			console.error("Logout failed (silent path):", error);
			safeLogError(error, "silent/isLogin=true");

			// Tetap bersihkan session lokal: API logout sering gagal saat token
			// sudah invalid/403 (Forbidden), tapi data USER/TOKEN harus hilang.
			const submateriProgress = JSON.parse(
				localStorage.getItem("SubmateriProgress") || "[]"
			);
			const seeTourDashboardCounter = localStorage.getItem("seeTourDashboardCounter");
			const isMobileValue = localStorage.getItem("isMobile");

			clearLocalStorageKeepGuestId();

			localStorage.setItem("SubmateriProgress", JSON.stringify(submateriProgress));
			if (seeTourDashboardCounter) {
				localStorage.setItem("seeTourDashboardCounter", seeTourDashboardCounter);
			}
			if (isMobileValue) {
				localStorage.setItem("isMobile", isMobileValue);
			}

			setGlobalLogoutLoading(false);
			window.location.href = "/login";
		}
		return true;
	} else {
		try {
			const result = await Swal.fire({
				title: "Apakah Anda yakin?",
				text: "Akun Anda akan keluar dari aplikasi",
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: "Ya, keluar!",
				cancelButtonText: "Tidak",
				confirmButtonColor: "#3C6F7D",
			});

			if (result.isConfirmed) {
				setGlobalLogoutLoading(true);
				await axiosInstance.post(`/api/auth/logout`);
				logAnalyticsEvent("logout", { method: "delete_token" });

				const submateriProgress = JSON.parse(
					localStorage.getItem("SubmateriProgress") || "[]"
				);
				const seeTourDashboardCounter = localStorage.getItem("seeTourDashboardCounter");
				const isMobileValue = localStorage.getItem("isMobile");

				clearLocalStorageKeepGuestId();

				localStorage.setItem("SubmateriProgress", JSON.stringify(submateriProgress));
				if (seeTourDashboardCounter) {
					localStorage.setItem("seeTourDashboardCounter", seeTourDashboardCounter);
				}
				if (isMobileValue) {
					localStorage.setItem("isMobile", isMobileValue);
				}

				window.location.href = "/login";
				return true;
			}
			return false;
		} catch (error) {
			console.error("Logout failed:", error);
			safeLogError(error, "confirm-dialog/isLogin=false");
			setGlobalLogoutLoading(false);
			Swal.fire({
				title: "Error",
				text: "Terjadi kesalahan saat keluar. Silakan coba lagi.",
				icon: "error",
			});
			return false;
		}
	}
};

export const logoutTentor = async () => {
	try {
		const result = await Swal.fire({
			title: "Apakah Anda yakin?",
			text: "Akun Anda akan keluar dari aplikasi",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Ya, keluar!",
			cancelButtonText: "Tidak",
			confirmButtonColor: "#3C6F7D",
		});

		if (result.isConfirmed) {
			// Broadcast logout loading state for full-screen overlay
			try {
				window.dispatchEvent(new CustomEvent("logout-loading", { detail: true }));
			} catch (error) {
				console.error("Failed to dispatch logout loading event:", error);
			}
			await axiosInstance.post(`/api/feature/class/tentor/logout`);
			logAnalyticsEvent("logout", { method: "delete_token" });

			const submateriProgress = JSON.parse(
				localStorage.getItem("SubmateriProgress") || "[]"
			);
			const seeTourDashboardCounter = localStorage.getItem("seeTourDashboardCounter");
			const isMobileValue = localStorage.getItem("isMobile");

			clearLocalStorageKeepGuestId();

			localStorage.setItem("SubmateriProgress", JSON.stringify(submateriProgress));
			if (seeTourDashboardCounter) {
				localStorage.setItem("seeTourDashboardCounter", seeTourDashboardCounter);
			}
			if (isMobileValue) {
				localStorage.setItem("isMobile", isMobileValue);
			}

			window.location.href = "/login";
		}
	} catch (error) {
		console.error("Logout failed:", error);
		// Reset global logout loading if there is an error
		try {
			window.dispatchEvent(new CustomEvent("logout-loading", { detail: false }));
		} catch (err) {
			console.error("Failed to dispatch logout loading event:", err);
		}
		Swal.fire({
			title: "Error",
			text: "Terjadi kesalahan saat keluar. Silakan coba lagi.",
			icon: "error",
		});
	}
};

export const deleteAccount = async () => {
	try {
		const result = await Swal.fire({
			title: "Yakin ingin menghapus akun?",
			text: "Tindakan ini tidak dapat dibatalkan. Seluruh data Anda akan dihapus permanen",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Ya, hapus akun saya",
			cancelButtonText: "Batal",
			confirmButtonColor: "#3C6F7D",
		});

		if (result.isConfirmed) {
			await axiosInstance.delete(`/api/feature/user/self-delete`);
			logAnalyticsEvent("logout", { method: "delete_account" });
			const isMobileValue = localStorage.getItem("isMobile");
			blockToolProgressPersist();
			localStorage.clear();
			if (isMobileValue) {
				localStorage.setItem("isMobile", isMobileValue);
			}
			Object.keys(Cookies.get()).forEach(function (cookieName) {
				Cookies.remove(cookieName);
			});

			let timerInterval;
			Swal.fire({
				icon: "success",
				title: "Akun Anda berhasil dihapus",
				timer: 2000,
				showConfirmButton: false,
				iconColor: "#3C6F7D",
				willClose: () => {
					clearInterval(timerInterval);
					window.location.href = "/login";
				}
			})

		}
	} catch (error) {
		console.error("Delete account failed:", error);
		Swal.fire({
			icon: "error",
			title: "Gagal",
			text: "Terjadi kesalahan saat menghapus akun."
		});
	}
};

export const getCurrentUser = () => {
	return JSON.parse(localStorage.getItem("USER") || "{}");
};

export const isLoggedIn = () => {
	return localStorage.getItem("TOKEN") ? true : false;
};

export const isParentLoggedIn = () => {
	return localStorage.getItem("PARENT_TOKEN") ? true : false;
}

export const isTentorLoggedIn = () => {
	return localStorage.getItem("TENTOR_TOKEN") ? true : false;
}

/* Forget Password */
export const forgetPassword = async (email: string) => {
	return await axiosInstance.get(
		"/api/auth/user/forgot-password/create?email=" + email
	);
};

export const resendForgetPassword = async (email: string) => {
	return await axiosInstance.get(
		"/api/auth/user/forgot-password/update?email=" + email
	);
};

export const validateForgetPassword = async (email: string, otp: string) => {
	return await axiosInstance.post("/api/auth/user/validate-forgot-password", {
		email: email,
		otp: otp,
	});
};

/* Forget Password Email */
export const sendOTPEmail = async (email: string) => {
	return await axiosInstance.get(
		"/api/auth/user/send-otp/email/create?email=" + email
	);
};

export const resendOTPEmail = async (email: string) => {
	return await axiosInstance.get(
		"/api/auth/user/send-otp/email/update?email=" + email
	);
};

export const validateOTPEmail = async (email: string, otp: string) => {
	return await axiosInstance.post("/api/auth/user/verify-otp/email", {
		email: email,
		otp: otp,
	});
};

export const updateForgetPassword = async (
	email: string,
	otp: string,
	password: string
) => {
	return await axiosInstance.post("/api/auth/user/update-forgot-password", {
		email: email,
		otp: otp,
		password: password,
	});
};

/* Verify Phone */
export const requestOTP = async () => {
	return await axiosInstance.get("/api/auth/user/register/send-otp");
};

export const requestResendOTP = async (phone: string) => {
	return await axiosInstance.get("/api/auth/user/register/resend-otp");
};

export const verifyOTP = async (otp: string) => {
	return await axiosInstance.post("/api/auth/user/register/verify-otp", {
		otp: otp,
	});
};